// src/context/AuthContext.tsx - Layer Defender Auth Context with TSO integration
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { TSOProfile } from '../types';

interface AuthContextType {
  user: User | null;
  profile: TSOProfile | null;
  isAuthenticated: boolean;
  loading: boolean;
  refreshProfile: () => Promise<TSOProfile | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<TSOProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshProfile = async (): Promise<TSOProfile | null> => {
    if (!user) return null;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      setProfile(data);
      return data;
    } catch (error) {
      console.error('Error in refreshProfile:', error);
      return null;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      
      // Clear any stored tokens
      localStorage.removeItem('sb-auth-token');
      Object.keys(localStorage).forEach(key => {
        if (key.includes('supabase.auth.token') || (key.includes('sb-') && key.includes('-auth-'))) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          setUser(session.user);
        }
      } catch (error) {
        console.error('Error getting session:', error);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event);
        
        if (session?.user) {
          setUser(session.user);
        } else {
          setUser(null);
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Load profile when user changes
  useEffect(() => {
    if (user) {
      refreshProfile();
    }
  }, [user]);

  // REDIRECT TO ACCOUNTS SYSTEM WHEN NOT AUTHENTICATED (Production only)
  useEffect(() => {
    // Wait for loading to complete
    if (loading) return;

    // SKIP REDIRECT FOR LOCALHOST DEVELOPMENT
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('localhost');
    
    if (isLocalhost) {
      console.log('Localhost detected - skipping auth redirect for development');
      return;
    }

    // If not authenticated, redirect back to accounts system (PRODUCTION ONLY)
    if (!user) {
      console.log('Not authenticated in Layer Defender, redirecting to accounts system');
      
      const currentDomain = window.location.hostname;
      const accountsUrl = `https://accounts.${currentDomain.replace(/^[^.]+\./, '')}/profile`;
      
      window.location.href = accountsUrl;
      return;
    }
  }, [loading, user]);

  const value = {
    user,
    profile,
    isAuthenticated: !!user,
    loading,
    refreshProfile,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};